import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useUserStore } from '@stores/user'
import { getGlobalLoginDialog } from '@/utils/useLoginDialog'

// 待跳转的目标路由
let pendingRoute: string | null = null

// 路由配置
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'PublicLayout',
    component: () => import('@views/layouts/PublicLayout.vue'),
    children: [
      {
        path: '',
        name: 'Home',
        component: () => import('@views/home/index.vue'),
        meta: { title: '首页', public: true },
      },
      {
        path: 'courses',
        name: 'CourseCenter',
        component: () => import('@views/course/index.vue'),
        meta: { title: '课程中心', public: true },
      },
      {
        path: 'course/:id',
        name: 'CourseDetail',
        component: () => import('@views/course/detail.vue'),
        meta: { title: '课程详情', public: true },
      },
    ],
  },
  {
    path: '/auth',
    name: 'Auth',
    component: () => import('@views/auth/index.vue'),
    meta: { title: '登录/注册', public: true },
  },
  {
    path: '/app',
    name: 'MainLayout',
    component: () => import('@views/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'MainLayoutRedirect',
        redirect: '/app/dashboard',
      },
      {
        path: 'learn',
        redirect: '/app/dashboard',
      },
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@views/dashboard/index.vue'),
        meta: { title: '学习仪表盘', icon: 'DataLine' },
      },
      {
        path: 'my-courses',
        name: 'MyCourses',
        component: () => import('@views/course/my-courses.vue'),
        meta: { title: '我的课程', icon: 'Collection' },
      },
      {
        path: 'learn/:courseId',
        name: 'Learn',
        component: () => import('@views/learn/index.vue'),
        meta: { title: '课程学习', hideSidebar: true },
      },
      {
        path: 'exam',
        name: 'ExamCenter',
        component: () => import('@views/exam/index.vue'),
        meta: { title: '考试中心', icon: 'DocumentChecked' },
      },
      {
        path: 'exam/:id',
        name: 'ExamTake',
        component: () => import('@views/exam/take.vue'),
        meta: { title: '正在考试', hideSidebar: true },
      },
      {
        path: 'exam/result/:id',
        name: 'ExamResult',
        component: () => import('@views/exam/result.vue'),
        meta: { title: '考试结果' },
      },
      {
        path: 'tasks',
        name: 'TaskCenter',
        component: () => import('@views/tasks/index.vue'),
        meta: { title: '任务中心', icon: 'List' },
      },
      {
        path: 'analytics',
        name: 'Analytics',
        component: () => import('@views/analytics/index.vue'),
        meta: { title: '数据中心', icon: 'TrendCharts' },
      },
      {
        path: 'ai-assistant',
        name: 'AIAssistant',
        component: () => import('@views/ai-assistant/index.vue'),
        meta: { title: 'AI助手', icon: 'ChatDotRound' },
      },
      {
        path: 'employment',
        name: 'CareerCenter',
        component: () => import('@views/employment/index.vue'),
        meta: { title: '就业中心', icon: 'Briefcase' },
      },
      {
        path: 'profile',
        name: 'Profile',
        component: () => import('@views/profile/index.vue'),
        meta: { title: '个人中心' },
      },
      {
        path: 'notifications',
        name: 'Notifications',
        component: () => import('@views/notifications/index.vue'),
        meta: { title: '通知中心' },
      },
      {
        path: 'learning-records',
        name: 'LearningRecords',
        component: () => import('@views/learning-records/index.vue'),
        meta: { title: '学习记录' },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@views/error/404.vue'),
    meta: { title: '页面不存在', public: true },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 }
  },
})

// 路由守卫
router.beforeEach(async (to, _from, next) => {
  const userStore = useUserStore()

  // 设置页面标题
  document.title = to.meta.title ? `${to.meta.title} - 学员学习门户` : '学员学习门户'

  // 公开页面直接放行
  if (to.meta.public) {
    next()
    return
  }

  // 需要登录的页面
  if (to.meta.requiresAuth && !userStore.isAuthenticated) {
    // 保存目标路由
    pendingRoute = to.fullPath

    // 显示登录弹窗
    const loginDialog = getGlobalLoginDialog()
    if (loginDialog) {
      const success = await loginDialog.showLoginDialog()
      if (success && pendingRoute) {
        // 登录成功，跳转到目标路由
        next(pendingRoute)
        pendingRoute = null
        return
      }
    }

    // 登录取消或失败，留在当前页面
    next(false)
    return
  }

  next()
})

// 登录成功后跳转到待跳转的路由
export function navigateToPendingRoute() {
  if (pendingRoute) {
    router.push(pendingRoute)
    pendingRoute = null
  }
}

// 设置待跳转路由
export function setPendingRoute(route: string) {
  pendingRoute = route
}

export default router
